// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable } from '@angular/core';
import { Workspace } from '@lfx/key-projects/key-projects.model';
import { WORKSPACE_NAMES } from '@lfx/key-projects/key-projects.constants';

/**
 * Result of workspace validation
 */
export interface WorkspaceValidationResult {
  shouldCreateDefaults: boolean;
  reason: string;
  defaultWorkspacesCount: number;
  hasOnlyMostActiveWorkspace: boolean;
}

/**
 * Service to validate default workspace creation requirements
 * Single Responsibility: Determine if default workspaces should be created
 */
@Injectable({
  providedIn: 'root',
})
export class DefaultWorkspaceValidationService {
  private readonly EXPECTED_DEFAULT_WORKSPACES: string[] = [
    WORKSPACE_NAMES.MOST_ACTIVE,
    WORKSPACE_NAMES.ALL_PROJECTS,
    WORKSPACE_NAMES.KEY_PROJECTS,
  ];

  private readonly MOST_ACTIVE_SIMILAR_NAME = 'My Most Active Projects';

  /**
   * Determine if default workspaces should be created
   *
   * Rules:
   * - If 2 or more of the 3 expected default workspaces exist → Skip creation
   * - If only "Most Active Projects" or "My Most Active Projects" exists → Trigger creation
   * - If 0 default workspaces exist → Trigger creation
   */
  shouldCreateDefaultWorkspaces(workspaces: Workspace[]): WorkspaceValidationResult {
    const defaultWorkspacesCount = this.countDefaultWorkspaces(workspaces);
    const hasOnlyMostActiveWorkspace = this.hasOnlyMostActiveWorkspace(workspaces);

    // Rule 1: If 2 or more default workspaces exist, skip creation
    if (defaultWorkspacesCount >= 2) {
      return {
        shouldCreateDefaults: false,
        reason: 'Two or more default workspaces already exist',
        defaultWorkspacesCount,
        hasOnlyMostActiveWorkspace: false,
      };
    }

    // Rule 2: If only "Most Active" or similar workspace exists, trigger creation
    if (hasOnlyMostActiveWorkspace) {
      return {
        shouldCreateDefaults: true,
        reason: 'Only Most Active workspace exists, creating full set of default workspaces',
        defaultWorkspacesCount,
        hasOnlyMostActiveWorkspace: true,
      };
    }

    // Rule 3: If 0 default workspaces exist, trigger creation
    if (defaultWorkspacesCount === 0) {
      return {
        shouldCreateDefaults: true,
        reason: 'No default workspaces exist, creating initial set',
        defaultWorkspacesCount,
        hasOnlyMostActiveWorkspace: false,
      };
    }

    // Edge case: 1 default workspace exists but it's not "Most Active"
    // Skip creation to preserve user's custom setup
    return {
      shouldCreateDefaults: false,
      reason: 'One non-Most-Active default workspace exists, preserving user setup',
      defaultWorkspacesCount,
      hasOnlyMostActiveWorkspace: false,
    };
  }

  /**
   * Count how many of the expected default workspaces exist
   */
  private countDefaultWorkspaces(workspaces: Workspace[]): number {
    return workspaces.filter(ws =>
      this.EXPECTED_DEFAULT_WORKSPACES.includes(ws.name)
    ).length;
  }

  /**
   * Check if only "Most Active Projects" or "My Most Active Projects" workspace exists
   * Returns true if exactly one workspace matches and no other default workspaces exist
   */
  private hasOnlyMostActiveWorkspace(workspaces: Workspace[]): boolean {
    const mostActiveWorkspaces = workspaces.filter(ws =>
      ws.name === WORKSPACE_NAMES.MOST_ACTIVE ||
      ws.name === this.MOST_ACTIVE_SIMILAR_NAME
    );

    const otherDefaultWorkspaces = workspaces.filter(ws =>
      ws.name === WORKSPACE_NAMES.ALL_PROJECTS ||
      ws.name === WORKSPACE_NAMES.KEY_PROJECTS
    );

    // Only one "Most Active" workspace and no other defaults
    return mostActiveWorkspaces.length === 1 && otherDefaultWorkspaces.length === 0;
  }

  /**
   * Get list of existing default workspace names
   */
  getExistingDefaultWorkspaceNames(workspaces: Workspace[]): string[] {
    return workspaces
      .filter(ws => this.EXPECTED_DEFAULT_WORKSPACES.includes(ws.name))
      .map(ws => ws.name);
  }

  /**
   * Check if all 3 default workspaces exist
   */
  allDefaultWorkspacesExist(workspaces: Workspace[]): boolean {
    return this.countDefaultWorkspaces(workspaces) === 3;
  }
}

